/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

export const HSE_DOCUMENT_TYPES = [
  "policy",
  "procedure",
  "instruction",
  "form",
  "guidance",
  "record",
  "template",
];

export const HSE_BULK_MAX_DOCUMENTS = 50;

function parseError(code, message) {
  const err = new Error(message || code);
  err.code = code;
  return err;
}

function normalizeDisciplineRef(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return { code: String(raw).trim() };
  }
  if (typeof raw !== "object") return null;
  const code = String(raw.code || raw.discipline_code || "").trim();
  const name = String(raw.name || raw.title || "").trim();
  if (!code && !name) return null;
  return {
    code: code || undefined,
    name: name || undefined,
    description: String(raw.description || "").trim() || undefined,
  };
}

function normalizeCategoryRef(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return { code: String(raw).trim() };
  }
  if (typeof raw !== "object") return null;
  const code = String(raw.code || raw.category_code || "").trim();
  const name = String(raw.name || raw.title || "").trim();
  if (!code && !name) return null;
  return {
    code: code || undefined,
    name: name || undefined,
    description: String(raw.description || "").trim() || undefined,
    chapters: Array.isArray(raw.chapters)
      ? raw.chapters.map(normalizeChapterRef).filter(Boolean)
      : undefined,
  };
}

function normalizeChapterRef(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return { number: String(raw).trim() };
  }
  if (typeof raw !== "object") return null;
  const number = String(raw.number || raw.chapter_number || "").trim();
  if (!number) return null;
  return {
    number,
    title: String(raw.title || raw.name || "").trim() || undefined,
    description: String(raw.description || "").trim() || undefined,
    parent_number: String(raw.parent_number || "").trim() || undefined,
  };
}

function normalizeDocumentRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || "").trim();
  if (!title) return null;

  const document_type = String(raw.document_type || "instruction")
    .trim()
    .toLowerCase();
  if (!HSE_DOCUMENT_TYPES.includes(document_type)) return null;

  const discipline =
    normalizeDisciplineRef(raw.discipline) ||
    normalizeDisciplineRef(raw.discipline_code) ||
    normalizeDisciplineRef(raw.discipline_name);
  const category =
    normalizeCategoryRef(raw.category) ||
    normalizeCategoryRef(raw.category_code) ||
    normalizeCategoryRef(raw.category_name);
  const chapter =
    normalizeChapterRef(raw.chapter) ||
    normalizeChapterRef(raw.chapter_number);

  if (!discipline || !category || !chapter) return null;

  const content_type =
    String(raw.content_type || "text").trim().toLowerCase() === "file"
      ? "file"
      : "text";

  return {
    title,
    description: String(raw.description || "").trim(),
    document_type,
    discipline_code: discipline.code || undefined,
    discipline: discipline,
    category_code: (category.code || "").toLowerCase() || undefined,
    category: category,
    chapter_number: chapter.number,
    chapter: chapter,
    content_type,
    content: content_type === "text" ? String(raw.content || "") : undefined,
  };
}

function normalizeStructureRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const discipline =
    normalizeDisciplineRef(raw.discipline) ||
    normalizeDisciplineRef(raw.discipline_code);
  if (!discipline) return null;
  const categories = Array.isArray(raw.categories)
    ? raw.categories.map(normalizeCategoryRef).filter(Boolean)
    : [];
  return { discipline, categories };
}

/**
 * Parse pasted HSE document bulk JSON.
 * Returns { payload fields..., droppedCount, dropReasons }.
 */
export function parseImportedHseDocumentsJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) throw parseError("empty");

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    throw parseError("invalid_json");
  }

  let create_missing_structure = false;
  let atomic = false;
  let access_type = "all";
  let user_ids = [];
  let structure = [];
  let documents = [];

  if (Array.isArray(data)) {
    documents = data;
  } else if (data && typeof data === "object") {
    create_missing_structure = Boolean(data.create_missing_structure);
    atomic = Boolean(data.atomic);
    access_type = String(data.access_type || "all").trim() || "all";
    user_ids = Array.isArray(data.user_ids)
      ? data.user_ids.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    structure = Array.isArray(data.structure) ? data.structure : [];
    if (Array.isArray(data.documents)) {
      documents = data.documents;
    } else if (data.document && typeof data.document === "object") {
      documents = [data.document];
    } else if (data.title && (data.document_type || data.chapter || data.chapter_number)) {
      documents = [data];
    }
  } else {
    throw parseError("invalid_shape");
  }

  if (documents.length > HSE_BULK_MAX_DOCUMENTS) {
    throw parseError(
      "too_many_documents",
      `Too many documents (max ${HSE_BULK_MAX_DOCUMENTS})`
    );
  }

  const dropReasons = [];
  const normalizedDocuments = [];
  documents.forEach((row, idx) => {
    const normalized = normalizeDocumentRow(row);
    if (normalized) {
      normalizedDocuments.push(normalized);
    } else {
      dropReasons.push(`documents[${idx}]`);
    }
  });

  const normalizedStructure = structure.map(normalizeStructureRow).filter(Boolean);
  const droppedStructure = structure.length - normalizedStructure.length;
  if (droppedStructure > 0) {
    dropReasons.push(`${droppedStructure} structure row(s)`);
  }

  if (normalizedStructure.length === 0 && normalizedDocuments.length === 0) {
    throw parseError("no_valid_items");
  }

  return {
    create_missing_structure,
    atomic,
    access_type,
    user_ids,
    structure: normalizedStructure,
    documents: normalizedDocuments,
    droppedCount: dropReasons.length,
    dropReasons,
  };
}

export function serializeDisciplineStructureForExport(disciplines = []) {
  const list = Array.isArray(disciplines) ? disciplines : [];
  return list.map((d) => ({
    code: d.code,
    name: d.name,
    categories: (d.categories || []).map((c) => ({
      code: c.code,
      name: c.name,
      chapters: flattenChapters(c.chapters || c.root_chapters || []),
    })),
  }));
}

function flattenChapters(chapters, parentNumber = null, acc = []) {
  for (const ch of chapters || []) {
    const number = ch.number;
    acc.push({
      number,
      title: ch.title,
      parent_number: parentNumber || undefined,
    });
    const nested = ch.sub_chapters || ch.subChapters || [];
    if (nested.length) {
      flattenChapters(nested, number, acc);
    }
  }
  return acc;
}

export function formatStructureOutline(disciplines = []) {
  const tree = serializeDisciplineStructureForExport(disciplines);
  if (!tree.length) {
    return "(Ingen firmaspesifikk struktur lastet ennå — sett create_missing_structure: true for å opprette.)";
  }
  const lines = [];
  for (const d of tree) {
    lines.push(`- Fag/Bransje: ${d.name} [${d.code}]`);
    for (const c of d.categories || []) {
      lines.push(`  - Kategori: ${c.name} [${c.code}]`);
      for (const ch of c.chapters || []) {
        const parent = ch.parent_number ? ` (under ${ch.parent_number})` : "";
        lines.push(`    - Kapittel ${ch.number}: ${ch.title || ""}${parent}`);
      }
    }
  }
  return lines.join("\n");
}

/**
 * Compact inventory of existing HSE documents for AI guides (avoid duplicates).
 * Accepts API list rows: title, document_type, document_number, status, discipline/category/chapter.
 */
export function formatExistingDocumentsOutline(documents = [], { max = 200 } = {}) {
  const list = Array.isArray(documents) ? documents : [];
  if (!list.length) {
    return "(Ingen dokumenter lastet — generer gjerne nye, men unngå generiske duplikater.)";
  }

  const sorted = [...list].sort((a, b) => {
    const at = String(a.title || "").localeCompare(String(b.title || ""), "nb");
    return at;
  });

  const lines = [];
  const shown = sorted.slice(0, max);
  for (const doc of shown) {
    const title = String(doc.title || "").trim() || "(uten tittel)";
    const type = String(doc.document_type || doc.document_type_display || "").trim();
    const number = String(doc.document_number || doc.full_document_number || "").trim();
    const status = String(doc.status || "").trim();
    const discipline =
      doc.discipline?.code ||
      doc.discipline?.name ||
      doc.discipline_code ||
      "";
    const category =
      doc.category?.code ||
      doc.category_code ||
      doc.category?.name ||
      "";
    const chapter =
      doc.chapter?.number ||
      doc.chapter_number ||
      doc.chapter ||
      "";
    const meta = [
      type || null,
      number || null,
      status || null,
      discipline ? `fag:${discipline}` : null,
      category ? `kat:${category}` : null,
      chapter ? `kap:${chapter}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    lines.push(`- ${title}${meta ? ` [${meta}]` : ""}`);
  }

  if (sorted.length > max) {
    lines.push(`… og ${sorted.length - max} flere (ikke listet).`);
  }
  lines.push(
    `Totalt: ${sorted.length} dokument(er). Do NOT create documents with the same (or near-identical) title/type/chapter as above unless the user explicitly asks to revise or replace.`
  );
  return lines.join("\n");
}

/** Compact JSON for AI context (titles + placement only). */
export function serializeExistingDocumentsForAi(documents = [], { max = 200 } = {}) {
  const list = Array.isArray(documents) ? documents : [];
  return list.slice(0, max).map((doc) => ({
    title: doc.title || "",
    document_type: doc.document_type || "",
    document_number: doc.document_number || doc.full_document_number || "",
    status: doc.status || "",
    discipline_code: doc.discipline?.code || doc.discipline_code || "",
    category_code: doc.category?.code || doc.category_code || "",
    chapter_number: doc.chapter?.number || doc.chapter_number || "",
  }));
}

export function serializeHseDocumentsPayloadForExport(payload = {}) {
  const out = {
    create_missing_structure: Boolean(payload.create_missing_structure),
    atomic: Boolean(payload.atomic),
    access_type: payload.access_type || "all",
    structure: payload.structure || [],
    documents: (payload.documents || []).map((doc) => ({
      title: doc.title || "",
      description: doc.description || "",
      document_type: doc.document_type || "instruction",
      discipline_code: doc.discipline_code || doc.discipline?.code || "",
      category_code: doc.category_code || doc.category?.code || "",
      chapter_number: doc.chapter_number || doc.chapter?.number || "",
      content_type: doc.content_type || "text",
      content: doc.content || "",
    })),
  };
  return JSON.stringify(out, null, 2);
}

export const SAMPLE_HSE_DOCUMENTS_JSON = `{
  "create_missing_structure": true,
  "atomic": false,
  "access_type": "all",
  "structure": [
    {
      "discipline": { "code": "VENT_UTF", "name": "Ventilasjon utførende" },
      "categories": [
        {
          "code": "hms",
          "name": "HMS",
          "chapters": [
            { "number": "6", "title": "Utførelse" },
            { "number": "6.1", "title": "Arbeidsinstrukser", "parent_number": "6" }
          ]
        }
      ]
    }
  ],
  "documents": [
    {
      "title": "Bruk av åndedrettsvern ved isolasjonsarbeid",
      "description": "Instruks for valg og bruk av åndedrettsvern.",
      "document_type": "instruction",
      "discipline_code": "VENT_UTF",
      "category_code": "hms",
      "chapter_number": "6.1",
      "content_type": "text",
      "content": "<h2>Formål</h2><p>Sikre korrekt bruk av åndedrettsvern.</p><h2>Ansvar</h2><p><span style=\\"color: red;\\">[Sett inn rolle]</span> er ansvarlig for opplæring.</p><h2>Fremgangsmåte</h2><ol><li>Velg filter etter stoffkartotek.</li><li>Utfør tetthetstest før arbeid.</li><li>Bytt filter etter produsentens anvisning.</li></ol>"
    }
  ]
}`;
